import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../../services/api';
import { BuildingOfficeIcon, TruckIcon, BanknotesIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

const VEHICLE_TYPES = [
  'Bicycle',
  'Motorbike',
  'Sedan',
  'SUV',
  'Pickup Truck',
  'Medium Van',
  '12-Wheeler',
  '18-Wheeler',
  'Motorboat',
  'Light Plane',
  'Helicopter',
  'Cargo Plane'
];

const MerchantRegister: React.FC = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    phone: '',
    business_name: '',
    business_address: '',
    company_registration_number: '',
    vehicle_types: [] as string[],
    bank_name: '',
    account_number: '',
    account_name: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [generatedCode, setGeneratedCode] = useState('');
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleVehicleTypeChange = (vehicleType: string) => {
    setFormData(prev => ({
      ...prev,
      vehicle_types: prev.vehicle_types.includes(vehicleType)
        ? prev.vehicle_types.filter(vt => vt !== vehicleType)
        : [...prev.vehicle_types, vehicleType]
    }));
  };

  const validateStep = (currentStep: number): boolean => {
    setError('');
    
    if (currentStep === 1) {
      if (!formData.first_name || !formData.last_name || !formData.email || !formData.phone) {
        setError('Please fill in all required fields');
        return false;
      }
      if (!formData.email.includes('@')) {
        setError('Please enter a valid email address');
        return false;
      }
    }
    
    if (currentStep === 2) {
      if (!formData.business_name || !formData.business_address || !formData.company_registration_number) {
        setError('Please fill in all business details');
        return false;
      }
      if (formData.vehicle_types.length === 0) {
        setError('Please select at least one vehicle type you can handle');
        return false;
      }
    }
    
    if (currentStep === 3) {
      if (!formData.bank_name || !formData.account_number || !formData.account_name) {
        setError('Please fill in all bank details');
        return false;
      }
      if (!formData.password || formData.password.length < 8) {
        setError('Password must be at least 8 characters long');
        return false;
      }
      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        return false;
      }
    }
    
    return true;
  };

  const nextStep = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    }
  };

  const prevStep = () => {
    setStep(step - 1);
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateStep(3)) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await authAPI.registerMerchant({
        email: formData.email,
        password: formData.password,
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone,
        business_name: formData.business_name,
        business_address: formData.business_address,
        company_registration_number: formData.company_registration_number,
        vehicle_types: formData.vehicle_types,
        bank_name: formData.bank_name,
        account_number: formData.account_number,
        account_name: formData.account_name,
      });

      const merchantCode = response.user?.merchant_info?.merchant_code || '';
      setGeneratedCode(merchantCode);
      setSuccess(true);
    } catch (err: any) {
      const errorDetail = err.response?.data?.detail;
      if (Array.isArray(errorDetail)) {
        const errorMessages = errorDetail.map((e: any) => 
          `${e.loc?.join(' > ') || 'Error'}: ${e.msg}`
        ).join(', ');
        setError(errorMessages);
      } else if (typeof errorDetail === 'string') {
        setError(errorDetail);
      } else {
        setError('Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="bg-green-50 border border-green-200 text-green-700 px-6 py-8 rounded-lg">
        <div className="text-center">
          <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-xl font-semibold mb-2">Registration Successful!</h3>
          <p className="text-sm mb-4">
            Your merchant account has been created and is pending approval.
          </p>
          {generatedCode && (
            <div className="bg-white border border-green-300 rounded-lg p-4 mb-4">
              <p className="text-sm text-gray-600 mb-2">Your Merchant Code:</p>
              <p className="text-2xl font-bold text-primary-600">{generatedCode}</p>
              <p className="text-xs text-gray-500 mt-2">
                Share this code with dispatchers who want to register under your company
              </p>
            </div>
          )}
          <button
            onClick={() => navigate('/login')}
            className="btn-primary mt-4"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border">
      <h3 className="text-xl font-semibold text-gray-900 mb-2">Merchant Registration</h3>
      <p className="text-gray-600 text-sm mb-6">Register as a Super Dispatcher to manage delivery operations</p>
      
      <div className="flex items-center justify-between mb-8">
        {[1, 2, 3].map((s) => (
          <div key={s} className="flex items-center">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-medium ${
              step >= s ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-600'
            }`}>
              {s}
            </div>
            {s < 3 && (
              <div className={`w-16 md:w-24 h-1 ${step > s ? 'bg-primary-600' : 'bg-gray-200'}`} />
            )}
          </div>
        ))}
      </div>
      
      <div className="text-center mb-6">
        <p className="text-sm font-medium text-gray-700">
          {step === 1 && 'Personal Information'}
          {step === 2 && 'Business Details'}
          {step === 3 && 'Bank & Security'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-3 py-2 rounded-md text-sm">
            {error}
          </div>
        )}
        
        {step === 1 && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="first_name" className="form-label">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="first_name"
                  name="first_name"
                  required
                  className="form-input"
                  value={formData.first_name}
                  onChange={handleChange}
                  placeholder="John"
                />
              </div>
              <div>
                <label htmlFor="last_name" className="form-label">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="last_name"
                  name="last_name"
                  required
                  className="form-input"
                  value={formData.last_name}
                  onChange={handleChange}
                  placeholder="Doe"
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className="form-label">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                className="form-input"
                value={formData.email}
                onChange={handleChange}
                placeholder="john@company.com"
              />
            </div>

            <div>
              <label htmlFor="phone" className="form-label">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                required
                className="form-input"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+234 xxx xxx xxxx"
              />
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <BuildingOfficeIcon className="h-5 w-5 text-primary-600" />
              <span className="font-medium text-gray-700">Company Information</span>
            </div>

            <div>
              <label htmlFor="business_name" className="form-label">
                Business Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="business_name"
                name="business_name"
                required
                className="form-input"
                value={formData.business_name}
                onChange={handleChange}
                placeholder="Kestol Logistics Ltd"
              />
              <p className="text-xs text-gray-500 mt-1">
                Your merchant code will be generated from this name
              </p>
            </div>

            <div>
              <label htmlFor="company_registration_number" className="form-label">
                CAC Registration Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="company_registration_number"
                name="company_registration_number"
                required
                className="form-input"
                value={formData.company_registration_number}
                onChange={handleChange}
                placeholder="RC123456"
              />
            </div>

            <div>
              <label htmlFor="business_address" className="form-label">
                Business Address <span className="text-red-500">*</span>
              </label>
              <textarea
                id="business_address"
                name="business_address"
                required
                rows={3}
                className="form-input"
                value={formData.business_address}
                onChange={handleChange}
                placeholder="Enter your business address"
              />
            </div>

            <div>
              <div className="flex items-center gap-2 mb-3">
                <TruckIcon className="h-5 w-5 text-primary-600" />
                <span className="font-medium text-gray-700">Vehicle Types You Can Handle</span>
                <span className="text-red-500">*</span>
              </div>
              <p className="text-xs text-gray-500 mb-3">
                Select all vehicle types your company can provide for deliveries
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {VEHICLE_TYPES.map((vt) => (
                  <label
                    key={vt}
                    className={`flex items-center gap-2 p-3 border rounded-lg cursor-pointer transition-colors ${
                      formData.vehicle_types.includes(vt)
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.vehicle_types.includes(vt)}
                      onChange={() => handleVehicleTypeChange(vt)}
                      className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">{vt}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <BanknotesIcon className="h-5 w-5 text-primary-600" />
              <span className="font-medium text-gray-700">Bank Information</span>
            </div>

            <div>
              <label htmlFor="bank_name" className="form-label">
                Bank Name <span className="text-red-500">*</span>
              </label>
              <select
                id="bank_name"
                name="bank_name"
                required
                className="form-input"
                value={formData.bank_name}
                onChange={handleChange}
              >
                <option value="">Select your bank</option>
                <option value="Access Bank">Access Bank</option>
                <option value="First Bank">First Bank of Nigeria</option>
                <option value="GTBank">Guaranty Trust Bank</option>
                <option value="UBA">United Bank for Africa</option>
                <option value="Zenith Bank">Zenith Bank</option>
                <option value="Sterling Bank">Sterling Bank</option>
                <option value="Union Bank">Union Bank</option>
                <option value="Fidelity Bank">Fidelity Bank</option>
                <option value="Stanbic IBTC">Stanbic IBTC Bank</option>
                <option value="Wema Bank">Wema Bank</option>
                <option value="Polaris Bank">Polaris Bank</option>
                <option value="Keystone Bank">Keystone Bank</option>
                <option value="Ecobank">Ecobank Nigeria</option>
                <option value="FCMB">First City Monument Bank</option>
                <option value="Opay">Opay</option>
                <option value="Palmpay">Palmpay</option>
                <option value="Kuda">Kuda Bank</option>
              </select>
            </div>

            <div>
              <label htmlFor="account_number" className="form-label">
                Account Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="account_number"
                name="account_number"
                required
                className="form-input"
                value={formData.account_number}
                onChange={handleChange}
                placeholder="0123456789"
                maxLength={10}
              />
            </div>

            <div>
              <label htmlFor="account_name" className="form-label">
                Account Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="account_name"
                name="account_name"
                required
                className="form-input"
                value={formData.account_name}
                onChange={handleChange}
                placeholder="Company or Personal Account Name"
              />
            </div>

            <div className="border-t pt-4 mt-4">
              <p className="text-sm font-medium text-gray-700 mb-4">Set Your Password</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="password" className="form-label">
                    Password <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    required
                    className="form-input"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Min. 8 characters"
                  />
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="form-label">
                    Confirm Password <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    required
                    className="form-input"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="Confirm your password"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-between pt-4">
          {step > 1 ? (
            <button
              type="button"
              onClick={prevStep}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Back
            </button>
          ) : (
            <div />
          )}

          {step < 3 ? (
            <button
              type="button"
              onClick={nextStep}
              className="btn-primary"
            >
              Continue
            </button>
          ) : (
            <button
              type="submit"
              disabled={loading}
              className="btn-primary disabled:opacity-50"
            >
              {loading ? 'Registering...' : 'Complete Registration'}
            </button>
          )}
        </div>
      </form>

      <div className="mt-6 text-center">
        <p className="text-sm text-gray-600">
          Already have an account?{' '}
          <button
            onClick={() => navigate('/login')}
            className="text-primary-600 hover:text-primary-700 font-medium"
          >
            Sign in
          </button>
        </p>
      </div>
    </div>
  );
};

export default MerchantRegister;
